import {ChevronDownIcon, ChevronRightIcon, CommentIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Banner, Button, SegmentedControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useMemo, useRef, useState} from 'react'
import {
  type AuthorFilterItem,
  CommentsFilter,
  type CommentsFilterState,
  getDefaultFilterState,
  useFilteredThreadIds,
  useFilterIssueCommentsIds,
} from './CommentsFilter'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {PageLimits} from '../../page-data/payloads/files'
import {ZeroState} from './ZeroState'
import {useInfiniteThreadPreviewsPageData} from '../../page-data/loaders/use-thread-previews'
import {useInfiniteIssueCommentsPageData} from '../../page-data/loaders/use-issue-comments'
import {isThreadPaginationEnabled} from '../PageLimitsBanner'
import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueCommentItem} from './IssueCommentItem'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {useMarkersActions, useThreads, useIssueComments, useGetThread} from '../../stores/selectors'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {ConversationCommentBox} from '@github-ui/diff-lines/conversation-comment-box'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {getItem, setItem} from '@github-ui/safe-storage/session-storage'
import type {IssueComment} from '../../page-data/payloads/issue-comments'
import {IDS} from '@github-ui/commenting/DomElements'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {useDockedPanel} from '../../contexts/DockedPanelContext'
import {createPortal} from 'react-dom'

interface CommentsSidePanelProps {
  onClose: () => void
  pageLimits: PageLimits
  pathName: string
  pullRequestAuthorLogin: string
  isOpen: boolean
  tabSize?: number
  filteredThreadIds: Set<string>
  diffCommentCount: number
  issueCommentsCount: number
}

interface CommentPanelHeaderContentProps {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  diffCommentCount: number
  issueCommentsCount: number
  threadPreviewsAuthorList: AuthorFilterItem[]
  issueCommentsAuthorList: AuthorFilterItem[]
  threadPreviewFilterState: CommentsFilterState
  setThreadPreviewFilterState: (state: CommentsFilterState) => void
  issueCommentsFilterState: CommentsFilterState
  setIssueCommentsFilterState: (state: CommentsFilterState) => void
}
/**
 * Renders the header content for the comments side panel, including a segmented control
 * for toggling between review (diff) comments and conversation (issue) comments views,
 * along with the corresponding filter controls for each view mode.
 *
 * The segmented control is only displayed when the `comments_panel_issue_comments` feature
 * flag is enabled. Each view mode has its own independent filter state and author list.
 *
 * @param viewMode - The currently active view mode (`'thread-previews'` or `'issue-comments'`).
 * @param setViewMode - Callback to update the active view mode.
 * @param diffCommentCount - The count of review (diff) comments, displayed as a badge on the "Review" tab.
 * @param issueCommentsCount - The count of issue (conversation) comments, displayed as a badge on the "Conversation" tab.
 * @param threadPreviewsAuthorList - List of authors available for filtering in the review comments view.
 * @param issueCommentsAuthorList - List of authors available for filtering in the conversation comments view.
 * @param threadPreviewFilterState - The current filter state for the review comments view.
 * @param setThreadPreviewFilterState - Callback to update the filter state for the review comments view.
 * @param issueCommentsFilterState - The current filter state for the conversation comments view.
 * @param setIssueCommentsFilterState - Callback to update the filter state for the conversation comments view.
 */
function CommentPanelHeaderContent({
  viewMode,
  setViewMode,
  diffCommentCount,
  issueCommentsCount,
  threadPreviewsAuthorList,
  issueCommentsAuthorList,
  threadPreviewFilterState,
  setThreadPreviewFilterState,
  issueCommentsFilterState,
  setIssueCommentsFilterState,
}: CommentPanelHeaderContentProps) {
  const issueCommentsEnabled = isFeatureEnabled('comments_panel_issue_comments')
  return (
    <div className="d-flex flex-row flex-items-center gap-2">
      {issueCommentsEnabled && (
        <SegmentedControl
          aria-label="Comment view mode"
          className="mt-2"
          onChange={(value: number) => {
            setViewMode(value === 0 ? 'thread-previews' : 'issue-comments')
          }}
        >
          <SegmentedControl.Button selected={viewMode === 'thread-previews'} count={diffCommentCount}>
            Review
          </SegmentedControl.Button>
          <SegmentedControl.Button selected={viewMode === 'issue-comments'} count={issueCommentsCount}>
            Conversation
          </SegmentedControl.Button>
        </SegmentedControl>
      )}
      {viewMode === 'thread-previews' && (
        <CommentsFilter
          authorList={threadPreviewsAuthorList}
          className="mt-2 width-full"
          filterState={threadPreviewFilterState}
          onFilterStateChange={setThreadPreviewFilterState}
          hideReviewThreadFilters={false}
        />
      )}
      {viewMode === 'issue-comments' && (
        <CommentsFilter
          authorList={issueCommentsAuthorList}
          className="mt-2 width-full"
          filterState={issueCommentsFilterState}
          onFilterStateChange={setIssueCommentsFilterState}
          hideReviewThreadFilters
        />
      )}
    </div>
  )
}

/**
 * Renders the body content of the Comments Side Panel.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded via `Suspense` with a skeleton fallback.
 *
 * @param viewMode - The current view mode, either `'thread-previews'` or another value for issue comments.
 * @param limitExceeded - Whether the number of comments exceeds the displayable limit.
 * @param limitCount - The maximum number of latest comments shown when the limit is exceeded.
 * @param isOpen - Whether the comments panel is currently open.
 * @param onClose - Callback invoked when the panel is closed.
 * @param tabSize - Optional tab size for rendering code snippets.
 * @param filteredThreadIds - The set of thread IDs to display after filtering has been applied.
 * @param pathName - The current file path name used to scope displayed comments.
 * @param pullRequestAuthorLogin - The login handle of the pull request author, used for issue comments rendering.
 */
function CommentsPanelBody({
  filteredIssueCommentIds,
  filteredThreadIds,
  handleQuoteReply,
  isOpen,
  issueComments,
  limitCount,
  limitExceeded,
  onClose,
  pathName,
  pullRequestAuthorLogin,
  tabSize,
  viewMode,
}: {
  filteredIssueCommentIds: Set<string>
  filteredThreadIds: Set<string>
  handleQuoteReply: (quotedText: string) => void
  isOpen: boolean
  issueComments: IssueComment[]
  limitCount: number
  limitExceeded: boolean
  onClose: () => void
  pathName: string
  pullRequestAuthorLogin: string
  tabSize?: number
  viewMode: ViewMode
}) {
  return (
    <>
      {viewMode === 'thread-previews' && limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
          className="tmp-mb-3"
        />
      )}
      <Suspense fallback={<ThreadPreviewSkeleton />}>
        {viewMode === 'thread-previews' ? (
          <ThreadPreviewsContent
            filteredThreadIds={filteredThreadIds}
            isOpen={isOpen}
            pathName={pathName}
            tabSize={tabSize}
            onClose={onClose}
          />
        ) : (
          <IssueCommentsContent
            filteredThreadIds={filteredIssueCommentIds}
            isOpen={isOpen}
            issueComments={issueComments}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            onQuoteReply={handleQuoteReply}
          />
        )}
      </Suspense>
    </>
  )
}

const collapseStateLocalStorageKey = 'pullRequestCommentsPanelFooterCollapseState'

interface CommentsSidePanelFooterProps {
  commentText: string
  onCommentTextChange: (value: string) => void
  onSubmitComment: () => void
  isPending: boolean
}

function CommentsSidePanelFooter({
  commentText,
  onCommentTextChange,
  onSubmitComment,
  isPending,
}: CommentsSidePanelFooterProps) {
  // TODO: if this becomes pinnable, we will have to come up with an SSR safe strategy that
  // avoids CLS. Might have to be a setting we store on the server and return in the payload
  const defaultCollapseState = getItem(collapseStateLocalStorageKey)
  const [isMinimized, setIsMinimized] = useState(defaultCollapseState === 'true')

  return (
    <div
      className="d-flex flex-column gap-2 width-full overflow-auto"
      style={{maxHeight: '50vh'}}
      id={IDS.issueCommentComposer}
    >
      <h4 className="d-flex">
        <IconButton
          aria-label={isMinimized ? 'Open PR comment panel' : 'Close PR comment panel'}
          icon={isMinimized ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          aria-expanded={!isMinimized}
          tooltipDirection="n"
          variant="invisible"
          className="mr-2"
          onClick={() => {
            const newState = !isMinimized
            setIsMinimized(newState)
            setItem(collapseStateLocalStorageKey, String(newState))
          }}
        />
        <span className="pb-2">Add a comment</span>
      </h4>
      {!isMinimized && (
        <ConversationCommentBox
          label="Add a comment"
          value={commentText}
          onChange={onCommentTextChange}
          onPrimaryAction={onSubmitComment}
          disabled={isPending}
        >
          <Button variant="primary" onClick={onSubmitComment} disabled={isPending || !commentText.trim()}>
            Comment
          </Button>
        </ConversationCommentBox>
      )}
    </div>
  )
}

type ThreadPreviewsContentProps = Pick<
  CommentsSidePanelProps,
  'filteredThreadIds' | 'onClose' | 'pathName' | 'isOpen' | 'tabSize'
>

/**
 * Content component that fetches and renders thread previews.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function ThreadPreviewsContent({filteredThreadIds, onClose, pathName, isOpen, tabSize}: ThreadPreviewsContentProps) {
  const {
    data,
    hasNextPage,
    isFetchingNextPage: isFetchingThreadPreviews,
    fetchNextPage,
  } = useInfiniteThreadPreviewsPageData({
    pathName,
  })

  const threadPreviews = useMemo(() => data.pages.flatMap(page => page.data), [data])

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingThreadPreviews && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        fetchingRef.current = false
      }
    }
    void fetchAllPages()
  }, [hasNextPage, isFetchingThreadPreviews, fetchNextPage])
  const getThreadFromStore = useGetThread()
  const {updateThread} = useMarkersActions()

  useEffect(() => {
    for (const thread of threadPreviews) {
      const filePath = thread.path ?? ''
      const isRightOrLeftSide = thread.subject?.endDiffSide === 'RIGHT' ? 'R' : 'L'
      const lineKey = thread.subject?.originalEndLine
        ? (`${isRightOrLeftSide}${thread.subject.originalEndLine}` as LineKey)
        : fileLevelCommentKey

      const storedThread = getThreadFromStore(Number(thread.id), filePath, lineKey)
      if (!storedThread) {
        updateThread(Number(thread.id), filePath, lineKey, () => {
          return {...thread, shouldNotRenderInDiffLines: true}
        })
      }
    }
  }, [getThreadFromStore, threadPreviews, updateThread])

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const handleNavigateToDiffComment = useCallback(() => {
    onClose()
  }, [onClose])

  const threadSummaries = threadPreviews
    .map(thread => {
      if (!thread || !filteredThreadIds.has(thread.id)) return null
      return (
        <ThreadPreview
          key={thread.id}
          tabSize={tabSize}
          thread={thread}
          onNavigateToDiffComment={handleNavigateToDiffComment}
          headingLevel="h2"
        />
      )
    })
    .filter(Boolean)

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isFetchingThreadPreviews) return
    const announceText =
      threadSummaries.length > 0
        ? `${threadSummaries.length} ${threadSummaries.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isFetchingThreadPreviews, debouncedAnnounce, threadSummaries])

  return (
    <>
      {threadSummaries.length > 0 ? (
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          <div className="d-flex flex-column position-relative width-full gap-3">
            {threadSummaries}
            {isFetchingThreadPreviews && <ThreadPreviewSkeleton count={1} />}
          </div>
        </Suspense>
      ) : isFetchingThreadPreviews ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasThreads ? 'No comments match the current filter' : 'No comments on changes yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

interface IssueCommentsContentProps extends Pick<
  CommentsSidePanelProps,
  'filteredThreadIds' | 'pathName' | 'isOpen' | 'pullRequestAuthorLogin'
> {
  issueComments: IssueComment[]
  onQuoteReply: (quotedText: string) => void
}

/**
 * Content component that fetches and renders issue comments.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function IssueCommentsContent({
  filteredThreadIds,
  pathName,
  isOpen,
  pullRequestAuthorLogin,
  issueComments,
  onQuoteReply,
}: IssueCommentsContentProps) {
  const {
    hasNextPage,
    isFetchingNextPage: isFetchingIssueComments,
    fetchNextPage,
  } = useInfiniteIssueCommentsPageData({
    pathName,
  })

  const hasIssueComments = issueComments.length > 0

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingIssueComments && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        fetchingRef.current = false
      }
    }
    void fetchAllPages()
  }, [hasNextPage, isFetchingIssueComments, fetchNextPage])

  const visibleIssueComments = useMemo(
    () => issueComments.filter(comment => filteredThreadIds.has(comment.id)),
    [issueComments, filteredThreadIds],
  )

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isFetchingIssueComments) return

    const announceText =
      visibleIssueComments.length > 0
        ? `${visibleIssueComments.length} ${visibleIssueComments.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`

    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isFetchingIssueComments, debouncedAnnounce, visibleIssueComments, hasIssueComments])
  const canReact = isLoggedIn()

  return (
    <>
      {visibleIssueComments.length > 0 ? (
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          <div className="d-flex flex-column position-relative width-full gap-3">
            {visibleIssueComments.map(comment => (
              <IssueCommentItem
                key={comment.id}
                comment={comment}
                pullRequestAuthorLogin={pullRequestAuthorLogin}
                canReact={canReact}
                onQuoteReply={onQuoteReply}
              />
            ))}
            {isFetchingIssueComments && <ThreadPreviewSkeleton count={1} />}
          </div>
        </Suspense>
      ) : isFetchingIssueComments ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasIssueComments ? 'No comments match the current filter' : 'No comments yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

type ViewMode = 'thread-previews' | 'issue-comments'

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments
 */
export const CommentsSidePanel = memo(function CommentsSidePanel(
  props: {
    toggleSidesheetRef: React.RefObject<HTMLElement | null>
  } & Omit<CommentsSidePanelProps, 'setThreadPreviews' | 'filteredThreadIds'>,
) {
  const {
    pathName,
    toggleSidesheetRef,
    pageLimits,
    pullRequestAuthorLogin,
    diffCommentCount,
    issueCommentsCount,
    isOpen,
    onClose,
    tabSize,
  } = props

  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const panelName = useDockedPanel()
  const commentsDockedSidePanelIsOpen = panelName === 'comments'
  // Get portal target - only accessed when panel is open, so SSR-safe
  const sidePanelHeader = commentsDockedSidePanelIsOpen
    ? ssrSafeDocument?.getElementById('docked-side-panel-header')
    : null
  const sidePanelBody = commentsDockedSidePanelIsOpen ? ssrSafeDocument?.getElementById('docked-side-panel-body') : null
  const sidePanelFooter = commentsDockedSidePanelIsOpen
    ? ssrSafeDocument?.getElementById('docked-side-panel-footer')
    : null

  const [viewMode, setViewMode] = useState<ViewMode>('thread-previews')
  const threadPreviews = useThreads()
  const issueComments = useIssueComments()

  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredThreadIds = useFilteredThreadIds(threadPreviews, threadPreviewFilterState)
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredIssueCommentIds = useFilterIssueCommentsIds(issueComments, issueCommentsFilterState)

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const isChronologicalOrder = isFeatureEnabled('pull_requests_chronological_comment_order')

  // State for comment footer
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending: isSubmittingComment} = useCreatePullRequestTimelineCommentMutation()

  const handleCommentTextChange = useCallback((value: string) => {
    setCommentText(value)
  }, [])

  const handleSubmitComment = useCallback(() => {
    if (!commentText.trim()) return
    createTimelineComment(
      {text: commentText},
      {
        onSuccess: data => {
          setCommentText('')
          // Scroll to bottom in chronological order mode after adding comment
          if (isChronologicalOrder) {
            requestAnimationFrame(() => {
              ssrSafeDocument
                ?.getElementById(`issue-comment-${data.issueComment.id}`)
                ?.scrollIntoView({behavior: 'smooth'})
            })
          }
        },
      },
    )
  }, [commentText, createTimelineComment, isChronologicalOrder])

  const handleQuoteReply = useCallback((quotedText: string) => {
    setCommentText(quotedText)
  }, [])

  const threadPreviewsAuthorList = useMemo(() => {
    const authors: AuthorFilterItem[] = []

    for (const threadPreview of threadPreviews) {
      const firstComment = getFirstComment(threadPreview)
      if (firstComment?.author && !authors.some(author => author.login === firstComment.author?.login)) {
        // Copilot goes to the top of the filter
        if (firstComment.author.login === 'Copilot') {
          authors.unshift(firstComment.author)
        } else {
          authors.push(firstComment.author)
        }
      }
    }

    return authors
  }, [threadPreviews])

  const issueCommentsAuthorList = useMemo(() => {
    const authors: AuthorFilterItem[] = []

    for (const comment of issueComments) {
      if (comment.authorLogin && !authors.some(author => author.login === comment.authorLogin)) {
        // Copilot goes to the top of the filter
        if (comment.authorLogin === 'Copilot') {
          authors.unshift({login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
        } else {
          authors.push({login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
        }
      }
    }

    return authors
  }, [issueComments])

  const isCommentPanelFooterEnabled = isFeatureEnabled('pr_comment_panel_footer') && viewMode === 'issue-comments'
  const commentPanelFooter = isCommentPanelFooterEnabled ? (
    <Dialog.Footer>
      <CommentsSidePanelFooter
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        onSubmitComment={handleSubmitComment}
        isPending={isSubmittingComment}
      />
    </Dialog.Footer>
  ) : null

  const threadPaginationEnabled = isThreadPaginationEnabled(pageLimits)
  const limitExceeded = threadPaginationEnabled
    ? pageLimits.reviewThreadsPaginatedLimitExceeded
    : pageLimits.reviewThreadsLimitExceeded
  const limitCount = threadPaginationEnabled ? pageLimits.reviewThreadsPaginatedLimit : pageLimits.reviewThreadsLimit

  if (!isOpen) return null

  if (isPrsDockedSidePanelEnabled && sidePanelHeader && sidePanelBody && sidePanelFooter) {
    return (
      <>
        {createPortal(
          <CommentPanelHeaderContent
            viewMode={viewMode}
            setViewMode={setViewMode}
            diffCommentCount={diffCommentCount}
            issueCommentsCount={issueCommentsCount}
            threadPreviewsAuthorList={threadPreviewsAuthorList}
            issueCommentsAuthorList={issueCommentsAuthorList}
            threadPreviewFilterState={threadPreviewFilterState}
            setThreadPreviewFilterState={setThreadPreviewFilterState}
            issueCommentsFilterState={issueCommentsFilterState}
            setIssueCommentsFilterState={setIssueCommentsFilterState}
          />,
          sidePanelHeader,
        )}
        {createPortal(
          <CommentsPanelBody
            viewMode={viewMode}
            filteredThreadIds={filteredThreadIds}
            filteredIssueCommentIds={filteredIssueCommentIds}
            issueComments={issueComments}
            handleQuoteReply={handleQuoteReply}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            limitCount={limitCount}
            isOpen={isOpen}
            onClose={onClose}
            tabSize={tabSize}
          />,
          sidePanelBody,
        )}
        {isCommentPanelFooterEnabled &&
          createPortal(
            <CommentsSidePanelFooter
              commentText={commentText}
              onCommentTextChange={handleCommentTextChange}
              onSubmitComment={handleSubmitComment}
              isPending={isSubmittingComment}
            />,
            sidePanelFooter,
          )}
      </>
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderFooter={() => {
        return commentPanelFooter
      }}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Comments</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
          <CommentPanelHeaderContent
            viewMode={viewMode}
            setViewMode={setViewMode}
            diffCommentCount={diffCommentCount}
            issueCommentsCount={issueCommentsCount}
            threadPreviewsAuthorList={threadPreviewsAuthorList}
            issueCommentsAuthorList={issueCommentsAuthorList}
            threadPreviewFilterState={threadPreviewFilterState}
            setThreadPreviewFilterState={setThreadPreviewFilterState}
            issueCommentsFilterState={issueCommentsFilterState}
            setIssueCommentsFilterState={setIssueCommentsFilterState}
          />
        </Dialog.Header>
      )}
    >
      <CommentsPanelBody
        viewMode={viewMode}
        limitExceeded={limitExceeded}
        limitCount={limitCount}
        isOpen={isOpen}
        issueComments={issueComments}
        onClose={onClose}
        tabSize={tabSize}
        filteredThreadIds={filteredThreadIds}
        filteredIssueCommentIds={filteredIssueCommentIds}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
      />
    </Dialog>
  )
})
